import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Truck, Store, CheckCircle2, Loader2, CalendarHeart, Clock, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "../components/SectionHeading";
import api, { formatApiError } from "../lib/api";
import { IMAGES } from "../data/content";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const inputCls = "w-full min-h-[48px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-charcoal outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition";

function prettyDate(iso) {
  if (!iso) return "an upcoming Friday";
  const d = new Date(iso + "T12:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

export default function FridayPlates() {
  const [week, setWeek] = useState(null);
  const [plates, setPlates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);              // configured line items
  const [configuring, setConfiguring] = useState(null); // { plate, rice, sides:{}, quantity, addons:{}, lineId? }
  const [method, setMethod] = useState("");
  const [delivery, setDelivery] = useState({ address: "", apt: "", city: "", zip: "", window: "" });
  const [info, setInfo] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/content/friday-menu").then((r) => { setWeek(r.data.week); setPlates(r.data.plates || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const open = week && week.ordering_open;
  const riceOptions = [...((week && week.rice_options) || []), "No Rice"];
  const sideOptions = (week && week.included_sides) || [];

  const startConfig = (p) => {
    const activeSizes = (p.sizes || []).filter((s) => s.active !== false);
    setConfiguring({ plate: p, size: activeSizes[0] || null, prep: (p.prep_options?.[0] || ""), rice: (week?.rice_options?.[0] || "No Rice"), sides: {}, quantity: 1, addons: {}, flavors: {} });
  };
  const isSoda = (name) => /soda/i.test(name || "");
  const cfgAddon = (name, delta) => setConfiguring((c) => {
    const q = Math.max(0, (c.addons[name] || 0) + delta);
    const flavors = { ...(c.flavors || {}) };
    if (q === 0) delete flavors[name];
    return { ...c, addons: { ...c.addons, [name]: q }, flavors };
  });
  const cfgQty = (delta) => setConfiguring((c) => ({ ...c, quantity: Math.max(1, c.quantity + delta) }));
  const priceLabel = (p) => {
    const sz = (p.sizes || []).filter((s) => s.active !== false);
    if (sz.length) return `From $${Math.min(...sz.map((s) => Number(s.price))).toFixed(2)}`;
    return `$${Number(p.price).toFixed(2)}`;
  };
  const addToCart = () => {
    const c = configuring, p = c.plate;
    const basePrice = c.size ? Number(c.size.price) : Number(p.price);
    const addons = (p.addons || []).filter((a) => c.addons[a.name] > 0).map((a) => ({ name: a.name, price: a.price, quantity: c.addons[a.name], ...(isSoda(a.name) && c.flavors?.[a.name] ? { flavor: c.flavors[a.name] } : {}) }));
    const sides = sideOptions;
    const item = { lineId: c.lineId || (Date.now() + Math.random()), plate_id: p.id, name: p.name, size: c.size ? c.size.name : "", prep: c.prep || "", price: basePrice, quantity: c.quantity, rice: c.rice, sides, addons };
    setCart((prev) => c.lineId ? prev.map((x) => (x.lineId === c.lineId ? item : x)) : [...prev, item]);
    setConfiguring(null);
  };
  const editLine = (it) => {
    const p = plates.find((x) => x.id === it.plate_id) || { id: it.plate_id, name: it.name, price: it.price, addons: [], sizes: [] };
    const size = (p.sizes || []).find((s) => s.name === it.size) || (it.size ? { name: it.size, price: it.price } : null);
    const sides = {}; (it.sides || []).forEach((s) => { sides[s] = true; });
    const addons = {}; (it.addons || []).forEach((a) => { addons[a.name] = a.quantity; });
    const flavors = {}; (it.addons || []).forEach((a) => { if (a.flavor) flavors[a.name] = a.flavor; });
    setConfiguring({ plate: p, size, prep: it.prep || (p.prep_options?.[0] || ""), rice: it.rice, sides, quantity: it.quantity, addons, flavors, lineId: it.lineId });
  };
  const removeLine = (lineId) => setCart((prev) => prev.filter((x) => x.lineId !== lineId));

  const total = useMemo(() => cart.reduce((sum, it) =>
    sum + it.price * it.quantity + it.addons.reduce((a, x) => a + x.price * x.quantity, 0), 0), [cart]);

  const canSubmit = cart.length > 0 && method &&
    info.first_name && info.last_name && info.email && info.phone &&
    (method !== "Delivery" || (delivery.address && delivery.city && delivery.zip));

  const submit = async () => {
    if (!canSubmit) { toast.error("Please complete your selection and contact details."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/friday-orders", {
        ...info, friday_date: week?.friday_date || "", items: cart.map(({ lineId, ...rest }) => rest),
        fulfillment_method: method, delivery: method === "Delivery" ? delivery : null,
      });
      setDone(data.order_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Submission failed."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="friday-plates-page" className="bg-bone">
      {/* HERO */}
      <section className="relative pt-28 lg:pt-36 pb-16 overflow-hidden">
        <img src={IMAGES.jerkPlatter} alt="Friday plate" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow text-gold mb-3">Friday Plates</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-white text-balance max-w-3xl">
            Fresh Plates, Every Friday.
          </h1>
          <p className="mt-5 text-platinum text-lg max-w-2xl">
            Order this week's Taste of Platinum plates for Friday pickup or delivery. Ordered during the week, prepared fresh for Friday.
          </p>
          {week && (
            <div data-testid="friday-schedule-card" className="mt-8 flex flex-col sm:flex-row gap-6 rounded-2xl border border-gold/40 bg-white/5 backdrop-blur-sm p-6 w-fit">
              <div className="flex items-center gap-3">
                <CalendarHeart className="w-8 h-8 text-gold" />
                <div><div className="eyebrow text-gold">Fulfillment</div>
                  <div className="text-white font-semibold" data-testid="friday-date">{prettyDate(week.friday_date)}</div></div>
              </div>
              <div className="hidden sm:block w-px bg-white/15" />
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-gold" />
                <div><div className="eyebrow text-gold">Order Deadline</div>
                  <div className="text-white font-semibold">{week.order_deadline || "Thursday"}</div></div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24 -mt-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-9">
            {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gold mx-auto" /></div> :
             done ? <FridayConfirmation orderId={done} friday={prettyDate(week?.friday_date)} /> :
             !week ? <div className="py-12 text-center text-slate-500" data-testid="friday-none">This week's Friday Plates menu isn't available yet. Please check back soon.</div> :
             !open ? (
               <div className="py-12 text-center" data-testid="friday-closed">
                 <div className="inline-block rounded-full bg-crimson/10 text-crimson px-5 py-2 text-sm font-semibold">Ordering is currently closed</div>
                 <p className="mt-4 text-slate-600">Ordering for {prettyDate(week.friday_date)} is closed. Please check back for next week's menu.</p>
                 <Link to="/meal-prep" className="mt-6 inline-flex items-center gap-2 text-charcoal font-semibold border-b-2 border-gold pb-1">Explore Meal Prep <ArrowRight className="w-4 h-4" /></Link>
               </div>
             ) : (
              <>
                {week.announcement && (
                  <div data-testid="friday-announcement" className="mb-6 rounded-xl bg-gold/10 border border-gold/30 p-4 text-sm text-charcoal">{week.announcement}</div>
                )}
                <SectionHeading eyebrow={`Menu for ${prettyDate(week.friday_date)}`} title="This Week's Plates"
                  subtitle="Select your plates and quantities, add any extras, then choose pickup or delivery." />

                <div data-testid="friday-plate-includes" className="mt-6 rounded-2xl border border-gold/40 bg-gold/5 p-5 text-sm text-charcoal">
                  <span className="font-semibold">Every Friday Plate includes</span> your entrée, choice of Rice &amp; Peas or White Rice, Plantains, and Vegetables. Additional sides are available for an extra charge.
                </div>

                {/* ENTRÉES */}
                <div className="mt-8 space-y-4" data-testid="friday-plates-list">
                  {plates.map((p) => {
                    const soldOut = p.status === "Sold Out";
                    return (
                      <div key={p.id} data-testid={`friday-plate-${p.id}`} className={`rounded-2xl border p-5 border-slate-200 ${soldOut ? "opacity-70" : ""}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-serif text-xl text-charcoal">{p.name}</h3>
                              {soldOut && <span data-testid={`friday-soldout-${p.id}`} className="rounded-full bg-crimson/10 text-crimson px-2.5 py-0.5 text-xs font-semibold">Sold Out</span>}
                            </div>
                            {p.description && <p className="mt-1 text-sm text-slate-600">{p.description}</p>}
                            <div className="mt-2 font-semibold text-gold">{priceLabel(p)}</div>
                          </div>
                          {soldOut ? <span className="text-sm text-slate-400 shrink-0">Unavailable</span> : (
                            <button data-testid={`friday-add-plate-${p.id}`} onClick={() => startConfig(p)}
                              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-sm font-semibold text-white hover:bg-charcoal/90">
                              <Plus className="w-4 h-4" /> Add Plate
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* YOUR ORDER (configured plates) */}
                {cart.length > 0 && (
                  <div className="mt-8" data-testid="friday-cart">
                    <h3 className="font-serif text-xl text-charcoal mb-3">Your Order</h3>
                    <div className="space-y-3">
                      {cart.map((it) => (
                        <div key={it.lineId} data-testid={`friday-cart-line-${it.plate_id}`} className="rounded-2xl border border-gold bg-gold/5 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm">
                              <div className="font-serif text-lg text-charcoal">{it.name}{it.size ? ` — ${it.size}` : ""} × {it.quantity}</div>
                              {it.prep && <div className="text-slate-600 text-xs">Preparation: <span className="font-medium">{it.prep}</span></div>}
                              <div className="text-slate-600">Rice: <span className="font-medium">{it.rice || "—"}</span></div>
                              <div className="text-slate-600">Included: <span className="font-medium">{it.sides.length ? it.sides.join(", ") : "Plantains, Vegetables"}</span></div>
                              <div className="text-slate-500 text-xs mt-0.5"><span className="font-medium text-charcoal">Paid add-ons:</span> {it.addons.length ? it.addons.map((a) => `${a.name} ×${a.quantity}${a.flavor ? ` — ${a.flavor}` : ""}`).join(", ") : "None"}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-semibold text-charcoal">${(it.price * it.quantity + it.addons.reduce((s, a) => s + a.price * a.quantity, 0)).toFixed(2)}</div>
                              <div className="mt-2 flex gap-2 justify-end">
                                <button data-testid={`friday-edit-line-${it.plate_id}`} onClick={() => editLine(it)} className="text-xs font-semibold text-charcoal border-b border-gold">Edit</button>
                                <button data-testid={`friday-remove-line-${it.plate_id}`} onClick={() => removeLine(it.lineId)} className="text-xs font-semibold text-crimson">Remove</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FULFILLMENT */}
                <div className="mt-8">
                  <span className="text-sm font-medium text-charcoal">Pickup or Delivery</span>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {week.pickup_available && (
                      <button data-testid="friday-method-pickup" onClick={() => setMethod("Pickup")}
                        className={`rounded-xl border-2 py-4 font-semibold flex items-center justify-center gap-2 transition ${method === "Pickup" ? "border-gold bg-gold/5 text-charcoal" : "border-slate-200 text-slate-600"}`}>
                        <Store className="w-4 h-4" /> Pickup
                      </button>
                    )}
                    {week.delivery_available && (
                      <button data-testid="friday-method-delivery" onClick={() => setMethod("Delivery")}
                        className={`rounded-xl border-2 py-4 font-semibold flex items-center justify-center gap-2 transition ${method === "Delivery" ? "border-gold bg-gold/5 text-charcoal" : "border-slate-200 text-slate-600"}`}>
                        <Truck className="w-4 h-4" /> Delivery
                      </button>
                    )}
                  </div>
                  {method === "Pickup" && (
                    <div data-testid="friday-pickup-note" className="mt-4 rounded-xl bg-emerald/5 border border-emerald/30 p-4 text-sm text-slate-700">
                      Pickup details will be provided with your confirmed order.
                    </div>
                  )}
                  {method === "Delivery" && (
                    <div data-testid="friday-delivery-fields" className="mt-4 grid sm:grid-cols-2 gap-4">
                      <label className="block sm:col-span-2"><span className="text-sm font-medium text-charcoal">Delivery Address *</span>
                        <input data-testid="friday-delivery-address" className={`${inputCls} mt-1.5`} value={delivery.address} onChange={(e) => setDelivery({ ...delivery, address: e.target.value })} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">Apt / Suite</span>
                        <input className={`${inputCls} mt-1.5`} value={delivery.apt} onChange={(e) => setDelivery({ ...delivery, apt: e.target.value })} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">City *</span>
                        <input data-testid="friday-delivery-city" className={`${inputCls} mt-1.5`} value={delivery.city} onChange={(e) => setDelivery({ ...delivery, city: e.target.value })} /></label>
                      <label className="block"><span className="text-sm font-medium text-charcoal">ZIP *</span>
                        <input data-testid="friday-delivery-zip" className={`${inputCls} mt-1.5`} value={delivery.zip} onChange={(e) => setDelivery({ ...delivery, zip: e.target.value })} /></label>
                      <label className="block sm:col-span-2"><span className="text-sm font-medium text-charcoal">Preferred Delivery Window</span>
                        <input className={`${inputCls} mt-1.5`} placeholder="e.g. Friday evening" value={delivery.window} onChange={(e) => setDelivery({ ...delivery, window: e.target.value })} /></label>
                    </div>
                  )}
                </div>

                {/* CUSTOMER INFO */}
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  <label className="block"><span className="text-sm font-medium text-charcoal">First Name *</span>
                    <input data-testid="friday-first-name" className={`${inputCls} mt-1.5`} value={info.first_name} onChange={(e) => setInfo({ ...info, first_name: e.target.value })} /></label>
                  <label className="block"><span className="text-sm font-medium text-charcoal">Last Name *</span>
                    <input data-testid="friday-last-name" className={`${inputCls} mt-1.5`} value={info.last_name} onChange={(e) => setInfo({ ...info, last_name: e.target.value })} /></label>
                  <label className="block"><span className="text-sm font-medium text-charcoal">Email *</span>
                    <input data-testid="friday-email" type="email" className={`${inputCls} mt-1.5`} value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} /></label>
                  <label className="block"><span className="text-sm font-medium text-charcoal">Phone *</span>
                    <input data-testid="friday-phone" className={`${inputCls} mt-1.5`} value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></label>
                  <label className="block sm:col-span-2"><span className="text-sm font-medium text-charcoal">Notes / Special Requests</span>
                    <textarea data-testid="friday-notes" rows={2} className={`${inputCls} mt-1.5`} value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} /></label>
                </div>

                {/* SUMMARY */}
                <div data-testid="friday-summary" className="mt-8 rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="bg-charcoal px-5 py-3 text-white font-serif text-lg">Order Total</div>
                  <div className="p-5 space-y-2 text-sm">
                    {cart.length === 0 ? <p className="text-slate-400">No plates added yet. Choose an entrée above to start.</p> : (
                      <>
                        {cart.map((it) => {
                          const addonTotal = it.addons.reduce((s, a) => s + a.price * a.quantity, 0);
                          return (
                          <div key={it.lineId} className="border-b border-slate-100 pb-2">
                            <div className="flex justify-between text-charcoal font-medium">
                              <span>{it.name}{it.size ? ` — ${it.size}` : ""} × {it.quantity}</span>
                              <span>${(it.price * it.quantity + addonTotal).toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-slate-500">{it.prep ? `${it.prep} · ` : ""}Rice: {it.rice} · Plate ${(it.price * it.quantity).toFixed(2)}</div>
                            <div className="text-xs text-slate-500">Included: {it.sides.length ? it.sides.join(", ") : "Plantains, Vegetables"}</div>
                            <div className="text-xs text-slate-500">Paid add-ons: {it.addons.length ? `${it.addons.map((a) => `${a.name} ×${a.quantity}`).join(", ")} ($${addonTotal.toFixed(2)})` : "None"}</div>
                          </div>
                          );
                        })}
                      </>
                    )}
                    <div className="flex justify-between font-semibold text-charcoal pt-1"><span>Estimated total</span><span data-testid="friday-total">${total.toFixed(2)}</span></div>
                    <p className="text-xs text-slate-400">Rice and included sides don't change the plate price. Submitting does not confirm your order — final total is confirmed at review and paid via a digital invoice we send you.</p>
                  </div>
                </div>

                <button data-testid="friday-submit" onClick={submit} disabled={!canSubmit || submitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 font-semibold text-charcoal hover:bg-gold-bright disabled:opacity-50">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit Order Request"}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* PLATE CONFIG MODAL */}
      {configuring && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid="friday-config-modal">
          <div className="absolute inset-0 bg-charcoal/70" onClick={() => setConfiguring(null)} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto thin-scroll">
            <div className="sticky top-0 z-10 bg-charcoal text-white px-6 py-4 flex items-center justify-between">
              <div><div className="eyebrow text-gold">Configure Plate</div><h3 className="font-serif text-lg">{configuring.plate.name}</h3></div>
              <button data-testid="friday-config-close" onClick={() => setConfiguring(null)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-6">
              {(configuring.plate.sizes || []).filter((s) => s.active !== false).length > 0 && (
                <div>
                  <div className="font-semibold text-charcoal">Choose Plate Size <span className="text-xs font-normal text-slate-400">(Choose 1)</span></div>
                  <div className="mt-2 space-y-2">
                    {(configuring.plate.sizes || []).filter((s) => s.active !== false).map((s) => (
                      <label key={s.id || s.name} data-testid={`friday-size-${slug(s.name)}`} className={`flex items-center justify-between rounded-xl border p-3 cursor-pointer ${configuring.size?.name === s.name ? "border-gold bg-gold/5" : "border-slate-200"}`}>
                        <span className="flex items-center gap-3">
                          <input type="radio" name="friday-size" className="w-4 h-4 accent-gold" checked={configuring.size?.name === s.name} onChange={() => setConfiguring({ ...configuring, size: s })} />
                          <span className="text-charcoal">{s.name}</span>
                        </span>
                        <span className="font-semibold text-gold">${Number(s.price).toFixed(2)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {(configuring.plate.prep_options || []).length > 0 && (
                <div>
                  <div className="font-semibold text-charcoal">Choose Preparation <span className="text-xs font-normal text-slate-400">(Choose 1)</span></div>
                  <div className="mt-2 space-y-2">
                    {configuring.plate.prep_options.map((pr) => (
                      <label key={pr} data-testid={`friday-prep-${slug(pr)}`} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${configuring.prep === pr ? "border-gold bg-gold/5" : "border-slate-200"}`}>
                        <input type="radio" name="friday-prep" className="w-4 h-4 accent-gold" checked={configuring.prep === pr} onChange={() => setConfiguring({ ...configuring, prep: pr })} />
                        <span className="text-charcoal">{pr}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="font-semibold text-charcoal">Choose Your Rice <span className="text-xs font-normal text-slate-400">(Choose 1 — included)</span></div>
                <div className="mt-2 space-y-2">
                  {riceOptions.map((r) => (
                    <label key={r} data-testid={`friday-rice-${slug(r)}`} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${configuring.rice === r ? "border-gold bg-gold/5" : "border-slate-200"}`}>
                      <input type="radio" name="friday-rice" className="w-4 h-4 accent-gold" checked={configuring.rice === r} onChange={() => setConfiguring({ ...configuring, rice: r })} />
                      <span className="text-charcoal">{r}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div data-testid="friday-included-note" className="rounded-xl bg-emerald/5 border border-emerald/30 p-3">
                <div className="font-semibold text-charcoal text-sm">Standard Included Items <span className="text-xs font-normal text-slate-400">(no extra charge)</span></div>
                <div className="mt-1 text-sm text-slate-600">{(sideOptions.length ? sideOptions : ["Plantains", "Vegetables"]).join(", ")}</div>
              </div>
              {(configuring.plate.addons || []).length > 0 && (
                <div>
                  <div className="font-semibold text-charcoal">Add-ons <span className="text-xs font-normal text-slate-400">(additional charge)</span></div>
                  <div className="mt-2 space-y-2">
                    {configuring.plate.addons.map((a) => (
                      <div key={a.name} className="rounded-xl border border-slate-200 p-3">
                        <div data-testid={`friday-addon-${slug(a.name)}`} className="flex items-center justify-between">
                          <span className="text-sm text-charcoal">{a.name} <span className="text-slate-400">+${Number(a.price).toFixed(2)}</span></span>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => cfgAddon(a.name, -1)} disabled={!configuring.addons[a.name]} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center disabled:opacity-40"><Minus className="w-4 h-4" /></button>
                            <span className="w-5 text-center font-semibold">{configuring.addons[a.name] || 0}</span>
                            <button type="button" onClick={() => cfgAddon(a.name, 1)} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                          </div>
                        </div>
                        {isSoda(a.name) && configuring.addons[a.name] > 0 && (
                          <div className="mt-3">
                            <label className="text-xs font-medium text-charcoal">Preferred Soda Flavor</label>
                            <input data-testid="friday-soda-flavor" className={`${inputCls} mt-1`} placeholder="Enter your preferred D&G soda flavor"
                              value={configuring.flavors?.[a.name] || ""} onChange={(e) => setConfiguring({ ...configuring, flavors: { ...configuring.flavors, [a.name]: e.target.value } })} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-semibold text-charcoal">Quantity <span className="text-xs font-normal text-slate-400">(same rice &amp; sides for all)</span></div>
                <div className="flex items-center gap-2">
                  <button data-testid="friday-config-qty-minus" onClick={() => cfgQty(-1)} className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <span data-testid="friday-config-qty" className="w-6 text-center font-semibold">{configuring.quantity}</span>
                  <button data-testid="friday-config-qty-plus" onClick={() => cfgQty(1)} className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
              <button data-testid="friday-config-add" onClick={addToCart} className="w-full rounded-full bg-gold px-6 py-3.5 font-semibold text-charcoal hover:bg-gold-bright">
                {configuring.lineId ? "Update Plate" : "Add to Order"} — ${(((configuring.size ? Number(configuring.size.price) : Number(configuring.plate.price)) * configuring.quantity) + (configuring.plate.addons || []).reduce((s, a) => s + (Number(a.price) || 0) * (configuring.addons[a.name] || 0), 0)).toFixed(2)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FridayConfirmation({ orderId, friday }) {
  return (
    <div data-testid="friday-confirmation" className="text-center py-4">
      <CheckCircle2 className="w-16 h-16 text-emerald mx-auto" />
      <h2 className="mt-5 font-serif text-3xl text-charcoal">Your Friday Plate Request Has Been Received!</h2>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto">Thank you for ordering with Taste of Platinum. Your request for {friday} is currently pending review.</p>
      <div className="mt-5 inline-block rounded-full bg-crimson/10 text-crimson px-5 py-2 text-sm font-semibold">Your order is not yet confirmed.</div>
      <div className="mt-6 inline-block rounded-2xl bg-bone px-8 py-4">
        <span className="text-sm text-slate-500">Order ID</span>
        <div className="font-serif text-3xl text-charcoal" data-testid="friday-order-id">{orderId}</div>
      </div>
      <div className="mt-6 max-w-xl mx-auto text-sm text-slate-600 space-y-2 text-left rounded-2xl border border-slate-200 p-5">
        <p>We'll review your order and follow up with confirmation, final pricing, and a digital invoice for payment.</p>
        <p>Your order is not confirmed until you receive confirmation from Taste of Platinum and payment has been completed.</p>
      </div>
      <div className="mt-8"><Link to="/" className="inline-flex items-center justify-center rounded-full border border-slate-300 px-7 py-3 font-semibold text-charcoal">Return Home</Link></div>
    </div>
  );
}
